import { adminApiConfig, getStoredAdminSession } from './admin-auth';
import type { PublicService } from './content-api';

export type AdminService = PublicService;

function requireSession() {
  const session = getStoredAdminSession();
  if (!session) throw new Error('Your admin session has expired. Sign in again.');
  return session;
}

async function adminRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const session = requireSession();
  const response = await fetch(`${adminApiConfig.url}/rest/v1/${path}`, {
    ...init,
    headers: {
      apikey: adminApiConfig.publishableKey,
      Authorization: `Bearer ${session.access_token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(detail || `Supabase request failed with status ${response.status}`);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

const serviceColumns = [
  'id','slug','name','category','description','starting_price','price_suffix',
  'duration_minutes','duration_label','deposit_percent','status','sort_order','is_featured',
].join(',');

export function getAdminServices() {
  return adminRequest<AdminService[]>(`services?select=${serviceColumns}&order=sort_order.asc`);
}

export function updateAdminService(id: string, changes: Partial<AdminService>) {
  return adminRequest<AdminService[]>(`services?id=eq.${encodeURIComponent(id)}&select=${serviceColumns}`, {
    method: 'PATCH',
    headers: { Prefer: 'return=representation' },
    body: JSON.stringify(changes),
  });
}

export function createAdminService(service: Omit<AdminService, 'id'>) {
  return adminRequest<AdminService[]>(`services?select=${serviceColumns}`, {
    method: 'POST',
    headers: { Prefer: 'return=representation' },
    body: JSON.stringify(service),
  });
}

export function makeServiceSlug(name: string) {
  const base = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'service';
  return `${base}-${Date.now().toString(36)}`;
}
